import React, { useMemo, useState } from 'react';
import { useSnapshot } from 'valtio';
import { Box, Button, Paper, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import ArrowForwardIcon from '@material-ui/icons/ArrowForward';
import { useTranslation } from 'react-i18next';
import { externalReadingFormats, getAvailableSettingNumber } from '../../Utils/info_utils';
import { formatDatabaseNumber, supportsDatabaseHistory } from '../../Utils/database_history_utils';
import { dayNightCo2PreheatMapping } from '../../Constants/constants';
import MeasurementChart from './MeasurementChart';
import usePreviewFeatures from '../../Hooks/usePreviewFeatures';

const useStyles = makeStyles((theme) => ({
  rooms: { display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: theme.spacing(2),
    [theme.breakpoints.down('sm')]: { gridTemplateColumns: 'minmax(0, 1fr)' } },
  room: { padding: theme.spacing(2), minWidth: 0 },
  metrics: { display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: theme.spacing(1),
    [theme.breakpoints.down('xs')]: { gridTemplateColumns: 'repeat(2, minmax(0, 1fr))' } },
  metric: { minWidth: 0, padding: theme.spacing(1), textAlign: 'left', textTransform: 'none',
    '& .MuiButton-label': { display: 'block' } },
  value: { color: theme.palette.primary.main, fontSize: 26, fontStyle: 'normal', fontVariantNumeric: 'tabular-nums' },
  unit: { fontSize: 12, marginLeft: 4, color: theme.palette.text.secondary }
}));

const types = [1, 2, 3, 16, 15];
const EMPTY = [];

function Room({ id, parent, room, available, range, onExplore }) {
  // Live values update only this room. Historical chart inputs stay memoized.
  const snapshot = useSnapshot(parent);
  const values = snapshot.infos ?? {};
  const classes = useStyles();
  const { t } = useTranslation();
  const metrics = useMemo(() => types.map((type) => {
    const metric = room?.measurements.find((item) => item.id === `${type}:0`);
    return { type, ...externalReadingFormats[type], series: metric?.series.filter((s) => s.main).map((s) => ({
      ...s, points: s.points.filter((point) => point.x >= range.start && point.x <= range.end)
    })).filter((s) => s.points.length) ?? EMPTY };
  }), [room, range.start, range.end]);
  const name = ((id === '' || String(snapshot.id) === id) && snapshot.name) || room?.name || (id ? `${t('database_axis_zone')} ${id}` : t('global'));
  const live = available && Boolean(snapshot.infos);
  const lastTime = metrics.reduce((at, metric) => Math.max(at, metric.series[0]?.points.at(-1)?.last_at ?? metric.series[0]?.points.at(-1)?.x ?? 0), 0);
  const phase = live ? values.phase : room?.phases.filter((point) => point.x >= range.start && point.x <= range.end).at(-1)?.y;
  return <Paper className={classes.room}>
    <Box display="flex" alignItems="center" justifyContent="space-between">
      <Typography variant="h6" component="h2">{name}</Typography>
      <Button size="small" aria-label={t('database_explore_room', { room: name })} onClick={() => onExplore(id, 'main')}><ArrowForwardIcon /></Button>
    </Box>
    <Box display="flex" justifyContent="space-between" flexWrap="wrap" mb={1}>
      <Typography variant="caption" color="textSecondary">{live ? t('database_live') : lastTime ? `${t('database_last_recorded')} · ${new Date(lastTime).toLocaleString()}` : t('database_live_unavailable')}</Typography>
      {dayNightCo2PreheatMapping[phase] && <Typography variant="caption" color="textSecondary">{t(dayNightCo2PreheatMapping[phase])}</Typography>}
    </Box>
    <div className={classes.metrics}>{metrics.filter((metric) => metric.type < 4 ||
      (live && metric.mainField in values) || metric.series.length).map((metric) => {
      const point = [...(metric.series[0]?.points ?? EMPTY)].reverse().find((p) => p.count !== undefined) ?? metric.series[0]?.points.at(-1);
      const raw = getAvailableSettingNumber(values[metric.mainField]);
      const value = live ? (raw == null ? null : raw / 10 ** metric.digits) : point?.last !== undefined ? point.last : point?.y;
      const target = live ? getAvailableSettingNumber(values[{ 1: 'info_temp_sp', 2: 'info_humidity_sp' }[metric.type]]) : null;
      return <Button key={metric.type} className={classes.metric} onClick={() => onExplore(id, `${metric.type}:0`)}
        aria-label={t('database_explore_measurement', { room: name, measurement: t(metric.label) })}>
        <Typography variant="body2" color="textSecondary">{t(metric.label)}</Typography>
        <span className={classes.value}>{formatDatabaseNumber(value, metric.digits)}</span><span className={classes.unit}>{metric.unit}</span>
        {target !== null && <Typography variant="caption" component="div" color="textSecondary">{t('database_target', { value: formatDatabaseNumber(target / 10 ** metric.digits, metric.digits) })}</Typography>}
        {metric.series.length > 0 && <MeasurementChart compact range={range} series={metric.series} unit={metric.unit} phases={room.phases} title={name + ' · ' + t(metric.label)} />}
      </Button>;
    })}</div>
  </Paper>;
}

export default function DashboardRooms({ controller, catalogue, zone, range, onExplore }) {
  const current = useSnapshot(controller);
  const previewEnabled = usePreviewFeatures();
  const classes = useStyles();
  const { t } = useTranslation();
  const [page, setPage] = useState(0);
  const scopes = supportsDatabaseHistory(current, undefined, previewEnabled) && current.type !== 5155 ? [''] : Object.keys(current.zones ?? {});
  const ids = [...new Set([...scopes, ...catalogue.map((room) => room.id)])].filter((id) => zone === 'all' || id === zone);
  const currentPage = Math.min(page, Math.max(0, Math.ceil(ids.length / 4) - 1));
  const available = current.connection?.status === 'good' && !current.connection?.awaiting_values;
  return <>
    {!ids.length && <Typography color="textSecondary">{t('database_empty')}</Typography>}
    <div className={classes.rooms}>{ids.slice(currentPage * 4, currentPage * 4 + 4).map((id) => <Room key={id} id={id}
      parent={controller.zones?.[id] ?? controller} room={catalogue.find((room) => room.id === id)} available={available && (Boolean(controller.zones?.[id]) || (!current.zones && id === ''))}
      range={range} onExplore={onExplore} />)}</div>
    {ids.length > 4 && <Box display="flex" justifyContent="flex-end" mt={2}>
      <Button disabled={!currentPage} onClick={() => setPage(currentPage - 1)}>{t('database_previous_rooms')}</Button>
      <Button disabled={(currentPage + 1) * 4 >= ids.length} onClick={() => setPage(currentPage + 1)}>{t('database_next_rooms')}</Button>
    </Box>}
  </>;
}
